import { FC } from 'react';
import { useFragment, graphql, readInlineData } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import RailTag from './RailTag';
import RailCreator from './RailCreator';

import styles from './PhotoRailContainer.scss';

import type { PhotoRailContainer_tradePhoto$key } from './__generated__/PhotoRailContainer_tradePhoto.graphql';
import type { PhotoRailContainer_searchBrowsePhoto$key } from './__generated__/PhotoRailContainer_searchBrowsePhoto.graphql';
import type { PhotoRailContainer_tags$key } from './__generated__/PhotoRailContainer_tags.graphql';
import type { PhotoRailContainer_creators$key } from './__generated__/PhotoRailContainer_creators.graphql';

type Props = {
    tradePhoto?: PhotoRailContainer_tradePhoto$key | null;
    searchBrowsePhoto?: PhotoRailContainer_searchBrowsePhoto$key | null;
};

const PhotoRailContainer: FC<Props> = ({
    searchBrowsePhoto: searchBrowsePhotoRef = null,
    tradePhoto: tradePhotoRef = null,
}) => {
    const searchBrowsePhoto = useFragment(
        graphql`
            fragment PhotoRailContainer_searchBrowsePhoto on PhotosSearchBrowsePhoto {
                tags {
                    ...PhotoRailContainer_tags
                }
                creators {
                    ...PhotoRailContainer_creators
                }
            }
        `,
        searchBrowsePhotoRef
    );

    const tradePhoto = useFragment(
        graphql`
            fragment PhotoRailContainer_tradePhoto on TradePhoto {
                tags {
                    ...PhotoRailContainer_tags
                }
                creators {
                    ...PhotoRailContainer_creators
                }
            }
        `,
        tradePhotoRef
    );

    const photo = searchBrowsePhoto || tradePhoto;
    const tagsRef = (photo?.tags || []).filter(filterNulls);
    const creatorsRef = (photo?.creators || []).filter(filterNulls);

    const tags = tagsRef.map(tagRef =>
        readInlineData<PhotoRailContainer_tags$key>(
            graphql`
                fragment PhotoRailContainer_tags on TradePhotoTag @inline {
                    serviceId
                    similarItems {
                        items {
                            serviceId
                        }
                    }
                    ...RailTag_tag
                }
            `,
            tagRef
        )
    );
    // !!! number of fetched items 'items(first: 4)' should be same in 'getShoppablePhotoData' file, otherwise we will double requests to BE
    const creators = creatorsRef.map(creatorRef =>
        readInlineData<PhotoRailContainer_creators$key>(
            graphql`
                fragment PhotoRailContainer_creators on PhotoCreator @inline {
                    serviceId
                    items(first: 4) {
                        ...RailCreator_items
                    }
                    ...RailCreator_creator
                }
            `,
            creatorRef
        )
    );

    const railCreators = (creators || [])
        .map(creator => {
            const creatorItems = creator.items || [];
            const creatorId = creator?.serviceId;
            if (creatorId && creatorItems.length > 0) {
                return (
                    <RailCreator
                        key={`creator-${creatorId}`}
                        creator={creator}
                        items={creatorItems.filter(filterNulls)}
                    />
                );
            }
            return null;
        })
        .filter(filterNulls);

    const railTags = (tags || [])
        .map(tag => {
            const hasSimilarItems = (tag?.similarItems?.items || []).length > 0;
            const tagId = tag?.serviceId;
            if (tagId && hasSimilarItems) {
                return <RailTag key={`tag-${tagId}`} tag={tag} />;
            }
            return null;
        })
        .filter(filterNulls);

    if (!railTags.length && !railCreators.length) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.whiteBackground} />
            <div className={styles.title}>
                <FormattedMessage
                    id="interiorPhoto.rail.tags.title"
                    defaultMessage="Get The Look"
                />
            </div>
            <div className={styles.railItems}>
                {railCreators}
                {railTags}
            </div>
        </div>
    );
};

export default PhotoRailContainer;
